import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowUpDown, ClipboardList, Inbox, Pencil, Trash2, User } from "lucide-react";

import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { WoStatusBadge } from "@/components/common/WoStatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RoleGate } from "@/components/auth/RoleGate";
import { workOrdersService } from "@/services/work_orders.service";
import { getErrorMessage } from "@/services/api";
import {
    OS_PRIORITIES, OS_PRIORITY_LABELS,
    WO_STATUSES, WO_STATUS_LABELS, WO_SORT_OPTIONS,
} from "@/config/constants";
import { formatDate } from "@/lib/utils";
import { usePermission } from "@/hooks/usePermission";
import { useAuth } from "@/contexts/AuthContext";

export default function WorkOrdersListPage() {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { can } = usePermission();
    const { user } = useAuth();
    const isAdmin = user?.role === "Administrator";
    const isTechnician = user?.role === "Technician";

    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [priority, setPriority] = useState("");
    const [tab, setTab] = useState("all"); // all | queue | pending
    const [sortKey, setSortKey] = useState("created_at:desc");
    const [confirm, setConfirm] = useState(null);

    // Resolve sortKey to backend params
    const sortOption = WO_SORT_OPTIONS.find((o) => o.value === sortKey) || WO_SORT_OPTIONS[0];
    const sort = { sortBy: sortOption.sortBy, sortOrder: sortOption.sortOrder };

    // Derive filter overrides based on quick tabs
    const effectiveStatus = tab === "pending" ? "enviada" : status;
    const createdByRole = tab === "queue" ? "Technician" : undefined;

    const query = useQuery({
        queryKey: ["work_orders", { page, search, status: effectiveStatus, priority, tab, ...sort }],
        queryFn: () => workOrdersService.list({
            page, pageSize: 20, search,
            sortBy: sort.sortBy, sortOrder: sort.sortOrder,
            status: effectiveStatus || undefined,
            priority: priority || undefined,
            created_by_role: createdByRole,
        }),
    });

    const del = useMutation({
        mutationFn: (id) => workOrdersService.remove(id),
        onSuccess: () => { toast.success("OS removida."); qc.invalidateQueries({ queryKey: ["work_orders"] }); },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao remover OS.")),
    });

    const columns = [
        {
            key: "number", header: "OS", sortable: true,
            cell: (r) => (
                <div className="flex items-center gap-3">
                    <div className="grid h-8 w-8 place-items-center rounded-md bg-muted text-muted-foreground">
                        <ClipboardList className="h-4 w-4" />
                    </div>
                    <div>
                        <div className="font-mono text-sm font-medium">{r.number}</div>
                        <div className="truncate text-xs text-muted-foreground max-w-[240px]">{r.title}</div>
                    </div>
                </div>
            ),
        },
        {
            key: "status", header: "Status",
            cell: (r) => <WoStatusBadge value={r.status} withDot />,
        },
        {
            key: "priority", header: "Prioridade",
            cell: (r) => OS_PRIORITY_LABELS[r.priority] || r.priority,
        },
        {
            key: "category", header: "Categoria",
            cell: (r) => <Badge variant="outline" className="text-[10px]">{r.category || "—"}</Badge>,
        },
        {
            key: "created_at", header: "Aberta em", sortable: true,
            cell: (r) => <span className="text-xs text-muted-foreground">{r.created_at ? formatDate(r.created_at) : "—"}</span>,
        },
        {
            key: "technicians", header: "Técnicos",
            cell: (r) => <span className="text-xs">{(r.technician_ids || []).length} designado{(r.technician_ids || []).length === 1 ? "" : "s"}</span>,
        },
        {
            key: "actions", header: "", className: "w-24",
            cell: (r) => (
                <div className="flex justify-end gap-1">
                    <RoleGate resource="work_orders" action="update">
                        <Button variant="ghost" size="icon" className="h-8 w-8"
                            onClick={(e) => { e.stopPropagation(); navigate(`/work-orders/${r.id}/edit`); }}
                            data-testid={`wo-edit-${r.id}`}>
                            <Pencil className="h-4 w-4" />
                        </Button>
                    </RoleGate>
                    <RoleGate resource="work_orders" action="delete">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-danger hover:text-danger"
                            onClick={(e) => { e.stopPropagation(); setConfirm(r); }}
                            data-testid={`wo-delete-${r.id}`}>
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </RoleGate>
                </div>
            ),
        },
    ];

    const filtersActive = [];
    if (status) filtersActive.push(`Status: ${WO_STATUS_LABELS[status] || status}`);
    if (priority) filtersActive.push(`Prioridade: ${OS_PRIORITY_LABELS[priority] || priority}`);
    if (sortKey !== "created_at:desc") filtersActive.push(`Ordenar: ${sortOption.label}`);

    return (
        <div className="space-y-6">
            <PageHeader
                title="Ordens de Serviço"
                description="Fluxo: Nova → Enviada ao técnico → Aceita → Em Atendimento → Finalizada."
                breadcrumb={[{ label: "Início", to: "/dashboard" }, { label: "OS" }]}
            />

            {/* Quick tabs */}
            <div className="flex flex-wrap gap-2">
                <Button
                    variant={tab === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => { setTab("all"); setPage(1); }}
                    data-testid="wo-tab-all"
                >
                    <ClipboardList className="mr-2 h-3.5 w-3.5" /> Todas
                </Button>
                {isAdmin && (
                    <Button
                        variant={tab === "queue" ? "default" : "outline"}
                        size="sm"
                        onClick={() => { setTab("queue"); setPage(1); }}
                        data-testid="wo-tab-queue"
                    >
                        <Inbox className="mr-2 h-3.5 w-3.5" />
                        Fila do Admin
                        <span className="ml-2 rounded-full bg-secondary/20 px-1.5 py-0.5 text-[10px] text-secondary">
                            criadas pelo técnico
                        </span>
                    </Button>
                )}
                {isTechnician && (
                    <Button
                        variant={tab === "pending" ? "default" : "outline"}
                        size="sm"
                        onClick={() => { setTab("pending"); setPage(1); }}
                        data-testid="wo-tab-pending"
                    >
                        <User className="mr-2 h-3.5 w-3.5" />
                        Aguardando meu aceite
                    </Button>
                )}
            </div>

            <DataTable
                dataTestId="work-orders"
                columns={columns}
                query={query.data}
                loading={query.isLoading}
                onSearch={(t) => { setPage(1); setSearch(t); }}
                onPageChange={setPage}
                onCreate={can("work_orders", "create") ? () => navigate("/work-orders/new") : null}
                createLabel="Nova OS"
                onRowClick={(r) => navigate(`/work-orders/${r.id}`)}
                emptyState={{ title: "Sem OS", description: "Nenhuma ordem de serviço cadastrada ainda.", cta: "Nova OS" }}
                filtersActive={filtersActive}
                toolbarLeft={
                    <>
                        <Select value={status || "__all__"} onValueChange={(v) => { setPage(1); setStatus(v === "__all__" ? "" : v === "__open__" ? "__open__" : v); }}>
                            <SelectTrigger className="h-9 w-40" data-testid="wo-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">Todos status</SelectItem>
                                <SelectItem value="__open__">Apenas abertas</SelectItem>
                                {WO_STATUSES.map((s) => (
                                    <SelectItem key={s.value} value={s.value}>
                                        <span className="flex items-center gap-2">
                                            <span className={`inline-block h-2 w-2 rounded-full ${s.dotClass}`} />
                                            {s.label}
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={priority || "__all__"} onValueChange={(v) => { setPage(1); setPriority(v === "__all__" ? "" : v); }}>
                            <SelectTrigger className="h-9 w-36" data-testid="wo-priority-filter"><SelectValue placeholder="Prioridade" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">Todas prioridades</SelectItem>
                                {OS_PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={sortKey} onValueChange={(v) => { setPage(1); setSortKey(v); }}>
                            <SelectTrigger className="h-9 w-56" data-testid="wo-sort-filter">
                                <span className="flex items-center gap-1.5">
                                    <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
                                    <SelectValue placeholder="Classificar por" />
                                </span>
                            </SelectTrigger>
                            <SelectContent>
                                {WO_SORT_OPTIONS.map((o) => (
                                    <SelectItem
                                        key={o.value}
                                        value={o.value}
                                        data-testid={`wo-sort-option-${o.sortBy}-${o.sortOrder}`}
                                    >
                                        {o.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </>
                }
            />
            <ConfirmDialog
                open={!!confirm}
                onOpenChange={(open) => !open && setConfirm(null)}
                title="Excluir OS?"
                description={`"${confirm?.number || ""}" será removida permanentemente.`}
                confirmLabel="Excluir" variant="destructive"
                onConfirm={() => { if (confirm) del.mutate(confirm.id); setConfirm(null); }}
            />
        </div>
    );
}
